/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { selectColor } from '@panel/commons/core';
import { getColor } from '@panel/commons/core/themev3';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { sendCustomTrackingInfo } from '@panel/commons/index';
import Checkbox from '@panel/commons/lib/Input/InputCheckbox';
import FinishReason from '@panel/commons/lib/Modals/FinishReason';
import { bumexFeaturesKeys } from '@panel/core/src/constants/bumexFeatures';
import { bumexFeatureSelectors } from '@panel/core/src/state/ducks/bumexFeatures';
import Atencion from '../../../assets/img/atencion.svg';
import { ArchiveOperations } from '../../../ducks/archive';
import { UnreadLeadsMsgOperations } from '../../../ducks/unreadLeadsMsg';

const ArchiveModal = ({
  postingIds,
  onClose,
  device,
  isDraft,
  isOnline,
  particularNotFree,
  publisherTypeId,
  finishReason,
  postings,
  posting,
  hasUnreadLeads
}) => {
  const [step, setStep] = useState(1);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [checkConfirmation, setCheckConfirmation] = useState(false);
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);
  // estados para finish reason
  const [finishReasonSelected, setFinishReasonSelected] = useState({});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [finishReasonText, setFinishReasonText] = useState(null);
  const isProfessional = publisherTypeId !== '1';
  // verificamos que en caso de error del back si es profesional que muestre el modal anterior
  const hasFinishReason = finishReason && finishReason.length;
  const publisherType = isProfessional ? 'profesionales' : 'particulares';
  const isOptionWithDescription =
    finishReasonSelected.motivo === 'other' ||
    finishReasonSelected.motivo === 'fraud' ||
    finishReasonSelected.motivo === 'infringement';
  const activeStatus = ['ONLINE', 'NEAR_END'];
  const comesFromArchive = true;
  const checkOnlinePostings = () => {
    if (postings) {
      postings.some(p => activeStatus.includes(p.stateAndDates[0].status));
    }
  };
  const hasOnlinePostings = isOnline || checkOnlinePostings();

  // se verifica que sea un solo aviso borrador en bulk mantiene la funcionalidad
  const isSingleDraft = postingIds.length === 1 && isDraft;
  const translateKey = 'panel.avisos.archive.modal.';
  const modaltext = isDraft ? (
    <Trans i18nKey='panel-modal-draftPost-text' data-panel='panel-modal-draftPost-text'>
      {t(`${translateKey}text${isSingleDraft ? '.draft' : postingIds.length > 1 ? '.plural' : ''}`)}
    </Trans>
  ) : (
    <Trans i18nKey='panel-modal-archivePost-text' data-panel='panel-modal-archivePost-text'>
      {t(
        `panel.avisos.archive.modal.text${isOnline ? '.active' : ''}${
          particularNotFree && isOnline ? '.particular' : ''
        }${postingIds.length > 1 ? '.plural' : ''}`
      )}
    </Trans>
  );

  const reasonsToShowUnreadLeadsModal = ['sell_rent', 'operation_cancelled', 'other'];
  const enabledUnreadLeadsModal =
    bumexFeatures.get(bumexFeaturesKeys.ENABLED_CHECK_UNREAD_LEADS) &&
    reasonsToShowUnreadLeadsModal.includes(finishReasonSelected.motivo) &&
    hasUnreadLeads;

  const dispatchArchive = () => {
    dispatch(
      ArchiveOperations.archivePosting(
        postingIds,
        isSingleDraft,
        particularNotFree,
        !isOnline,
        finishReasonSelected.id,
        isOptionWithDescription ? finishReasonText : null
      )
    );
    onClose();
    sendCustomTrackingInfo({
      event: 'trackEvent',
      eventCategory: 'Panel',
      eventAction: 'Mis avisos',
      eventLabel: 'Archivar Aviso-archivar'
    });
    if (enabledUnreadLeadsModal) {
      dispatch(UnreadLeadsMsgOperations.setShowUnreadLeadsModal(true, postingIds, comesFromArchive));
    }
  };

  const handleOnClick = () => (hasOnlinePostings && !isProfessional ? setStep(2) : dispatchArchive());

  useEffect(() => {
    if (isOptionWithDescription && finishReasonText) {
      // se valida en el caso de motivo con descripcion el min y max de caracteres
      return setButtonDisabled(finishReasonText.length < 3 || finishReasonText.length > 256);
    }
    return setButtonDisabled(!finishReasonSelected.motivo || isOptionWithDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishReasonSelected, finishReasonText]);

  const onChangeText = e => setFinishReasonText(e.target.value);

  const listOfPostingSelected = () => {
    if (postings) {
      const filteredPostings = postings.filter(p => postingIds.includes(p.postingId));
      return filteredPostings.every(currentValue => currentValue.isDuplicate);
    }
    return posting.isDuplicate;
  };

  return (isProfessional && hasFinishReason && hasOnlinePostings) || (step === 1 && hasOnlinePostings) ? (
    <FinishReason
      hasDuplicate={listOfPostingSelected()}
      bulkIds={postingIds}
      device={device}
      finishReason={finishReason}
      handleOnClick={handleOnClick}
      buttonDisabled={buttonDisabled}
      finishReasonSelected={finishReasonSelected}
      setFinishReasonSelected={setFinishReasonSelected}
      onChangeText={onChangeText}
      publisherType={publisherType}
      isOptionWithDescription={isOptionWithDescription}
      isArchiveAction
      ctaText={!isProfessional ? 'Continuar' : null}
      isProfessional={isProfessional}
    />
  ) : (
    <Container
      device={device}
      data-panel={`panel-modal-${isDraft ? 'draftPost' : 'archivePost'}-container`}
      isSecondStep={step === 2}
    >
      <Img
        src={Atencion}
        data-panel={`panel-modal-${isDraft ? 'draftPost' : 'archivePost'}-image`}
        isSecondStep={step === 2}
      />
      <ModalContent isSecondStep={step === 2}>
        <h3 data-panel={`panel-modal-${isDraft ? 'draftPost' : 'archivePost'}-title`}>
          {t(`${translateKey}title${isSingleDraft ? '.draft' : postingIds.length > 1 ? '.plural' : ''}`, {
            value: postingIds.length
          })}
        </h3>
        <ModalTextContainer>{modaltext}</ModalTextContainer>
        {particularNotFree && isOnline && (
          <ParticularCheckContainer onClick={() => setCheckConfirmation(!checkConfirmation)}>
            <Checkbox
              onChange={() => setCheckConfirmation(!checkConfirmation)}
              name='archive-particular-confirmation'
              checked={checkConfirmation}
              data-panel={`panel-modal-${isDraft ? 'draftPost' : 'archivePost'}-particularCheck`}
            />
            <span>{t(`panel.avisos.archive.modal.checkbox${postingIds.length > 1 ? '.plural' : ''}`)}</span>
          </ParticularCheckContainer>
        )}
      </ModalContent>
      <CTA
        disabled={particularNotFree && isOnline && !checkConfirmation}
        onClick={dispatchArchive}
        data-panel={`panel-modal-${isDraft ? 'draftPost' : 'archivePost'}-buttonCTA`}
      >
        {t(`${translateKey}CTA${isSingleDraft ? '.draft' : postingIds.length > 1 ? '.plural' : ''}`)}
      </CTA>
    </Container>
  );
};

const Container = styled.div`
  padding: ${({ device }) => (device === 'mobile' ? '32px 42px' : '40px')};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  max-width: 400px;
  height: 'fit-content';
  h3 {
    font-family: Hind;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: 0em;
    text-align: center;
    white-space: normal;
    margin-bottom: 8px;
  }
  p {
    font-family: Hind;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px;
    letter-spacing: 0em;
    text-align: center;
    white-space: normal;
    color: ${selectColor('grayDark')};
    margin-bottom: 32px;
    max-width: 400px;
  }
`;

const Img = styled.div`
  background-image: url(${({ src }) => src});
  width: 145px;
  height: 96px;
  ${({ isSecondStep }) =>
    isSecondStep
      ? css`
          margin-top: 10px;
        `
      : css`
          margin-bottom: 32px;
        `}
`;

const CTA = styled.button`
  height: 40px;
  width: 94%;
  border-radius: 8px;
  color: white;
  background-color: ${getColor('primary')};
  padding: 0px 12px;
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
  cursor: pointer;
  ${({ disabled }) =>
    disabled
      ? css`
          background-color: ${selectColor('grayLight')};
          color: ${selectColor('grayMediumLight')};
          cursor: auto;
        `
      : css`
          :hover {
            background-color: ${getColor('secondary')};
          }
        `};
`;

const ParticularCheckContainer = styled.div`
  display: flex;
  align-items: start;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid ${selectColor('grayLight')};
  justify-content: center;
  white-space: normal;
  cursor: pointer;
  width: 94%;
  > div {
    display: inline-flex;
    margin-right: 8px;
    margin-top: 2px;
  }
`;

const ModalTextContainer = styled.div`
  width: 94%;
  box-sizing: border-box;
  white-space: break-spaces;
  text-align: center;
`;

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  > h3 {
    margin-bottom: 24px;
    margin-top: ${({ isSecondStep }) => (isSecondStep ? 30 : 0)}px;
  }
`;

export default ArchiveModal;
